import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { Toggle } from '@/components/admin/FormComponents';
import { StatusPill, type PillTone } from '@/components/cccd/StatusPill';
import { rollbackOptimistic } from '@/lib/optimisticMutation';
import { cccdScoring, type CccdLaunchStatus, type LaunchOrderTeam } from '@/lib/api/cccdScoringClient';
import type { EventCategory } from '@/lib/api/cccdAdminClient';
import { toastError, toastSuccess } from '@/lib/toast';
import { readActiveCategoryId, rememberActiveCategoryId } from '@/lib/cccdActiveCategory';
import { LoadState } from "@/components/ui/LoadState"

const ITEM_TYPE = 'cccd-launch-kanban-card';

const LAUNCH_STATUS_LABELS: Record<CccdLaunchStatus, string> = {
  sin_estado: 'Sin estado',
  por_programar: 'Por programar',
  programado: 'Programado',
  proximo_a_lanzar: 'Próximo a lanzar',
  en_lanzamiento: 'En lanzamiento',
  lanzado: 'Lanzado',
  no_volo: 'No voló',
  en_revision: 'En revisión',
};

const LAUNCH_STATUS_TONES: Record<CccdLaunchStatus, PillTone> = {
  sin_estado: 'gray',
  por_programar: 'gray',
  programado: 'brand',
  proximo_a_lanzar: 'warning',
  en_lanzamiento: 'warning',
  lanzado: 'success',
  no_volo: 'error',
  en_revision: 'gray',
};

const LAUNCH_STATUSES = Object.keys(LAUNCH_STATUS_LABELS) as CccdLaunchStatus[];

interface DragItem {
  id: string;
}

// Tarjeta arrastrable de un equipo — soltarla sobre otra tarjeta la ubica
// justo antes de esa tarjeta (y adopta su estado/columna); soltarla en el
// cuerpo vacío de una columna la manda al final de esa columna. El mismo
// arreglo plano `teams` (un solo launchOrder compartido por toda la
// categoría, ver comentario más abajo) sigue siendo la fuente de verdad —
// esto solo cambia cómo se agrupa visualmente.
function KanbanCard({
  team,
  onHoverCard,
  onDragStart,
  onDrop,
  onCancel,
  onChangeStatus,
}: {
  team: LaunchOrderTeam;
  onHoverCard: (dragId: string, beforeId: string, status: CccdLaunchStatus) => void;
  onDragStart: () => void;
  onDrop: () => void;
  /** El arrastre terminó sin soltarse en una zona válida (Esc o fuera): se descarta lo simulado. */
  onCancel: () => void;
  /** Cambio de estado sin arrastrar (pantallas táctiles y teclado). */
  onChangeStatus: (status: CccdLaunchStatus) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<DragItem>({
    accept: ITEM_TYPE,
    hover(item) {
      if (item.id === team.id) return;
      onHoverCard(item.id, team.id, team.launchStatus);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: (): DragItem => {
      onDragStart();
      return { id: team.id };
    },
    end: (_item, monitor) => (monitor.didDrop() ? onDrop() : onCancel()),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`cursor-grab rounded-lg border border-gray-100 bg-white p-3 shadow-theme-xs active:cursor-grabbing dark:border-gray-800 dark:bg-white/[0.03] ${isDragging ? 'opacity-40' : ''}`}
    >
      <p className="text-[13px] font-medium text-gray-800 dark:text-white/90">{team.name}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{team.institution?.name ?? 'Sin institución'}</p>
      {/* El arrastre no funciona al tacto: este selector cambia el estado por el mismo camino que soltar la tarjeta. */}
      <select
        aria-label={`Estado de ${team.name}`}
        value={team.launchStatus}
        onChange={(e) => onChangeStatus(e.target.value as CccdLaunchStatus)}
        className="mt-2 min-h-11 w-full rounded-lg border border-gray-300 bg-white px-2 text-xs text-gray-700 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
      >
        {LAUNCH_STATUSES.map((status) => (
          <option key={status} value={status}>
            {LAUNCH_STATUS_LABELS[status]}
          </option>
        ))}
      </select>
    </div>
  );
}

// Zona de una columna (debajo de la última tarjeta, o toda la columna si
// está vacía) — soltar ahí manda el equipo al final de esta columna/estado.
function KanbanColumnDropZone({
  status,
  onHoverColumn,
}: {
  status: CccdLaunchStatus;
  onHoverColumn: (dragId: string, status: CccdLaunchStatus) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: ITEM_TYPE,
    hover(item, monitor) {
      if (!monitor.isOver({ shallow: true })) return;
      onHoverColumn(item.id, status);
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
  });
  drop(ref);

  return <div ref={ref} className={`min-h-16 flex-1 rounded-lg transition-colors ${isOver ? 'bg-brand-50/60 dark:bg-brand-500/[0.06]' : ''}`} />;
}

// Control de lanzamiento del día del evento (cccd-scoring-admin-to-gestion)
// — vista Kanban por estado de lanzamiento (cccd-scoring-ux-redesign):
// arrastrar una tarjeta a otra columna cambia su estado; arrastrarla dentro
// de la misma columna reordena. Por debajo sigue siendo UN solo arreglo
// ordenado por launchOrder compartido por toda la categoría (no por
// columna) — igual que antes de ser Kanban, ver getPublicLaunchControlState
// en cccd-launch-control.ts, que usa ese mismo orden plano para "quién
// sigue" en las páginas públicas. Las columnas son solo una agrupación
// visual de ese arreglo por su launchStatus actual; arrastrar entre
// columnas reescribe el launchStatus del equipo movido y reubica su
// posición dentro del arreglo, sin tocar el de los demás.
export function LaunchControlSection({ categories }: { categories: EventCategory[] }) {
  const qc = useQueryClient();
  const scopeId = categories[0]?.eventId ?? categories[0]?.regionalId;
  const [activeCategoryId, setActiveCategoryId] = useState(() => readActiveCategoryId(scopeId, categories));
  useEffect(() => {
    if (!activeCategoryId && categories[0]) setActiveCategoryId(readActiveCategoryId(scopeId, categories));
  }, [categories, activeCategoryId, scopeId]);
  useEffect(() => {
    rememberActiveCategoryId(scopeId, activeCategoryId);
  }, [scopeId, activeCategoryId]);

  const orderKey = ['launch-order', activeCategoryId];
  const { data: teams = [], isLoading } = useQuery({
    queryKey: orderKey,
    queryFn: () => cccdScoring.getCategoryLaunchOrder(activeCategoryId),
    enabled: !!activeCategoryId,
  });

  const activeCategory = categories.find((c) => c.id === activeCategoryId);

  const invalidateOrder = () => qc.invalidateQueries({ queryKey: orderKey });

  const liveMutation = useMutation({
    mutationFn: (isLive: boolean) => cccdScoring.setCategoryLive(activeCategoryId, isLive),
    onSuccess: (_result, isLive) => {
      const previousLive = categories.find((c) => c.isLive && c.id !== activeCategoryId);
      if (!activeCategory) toastSuccess('Actualizado.');
      else if (isLive) {
        toastSuccess(
          previousLive
            ? `"${activeCategory.name}" ahora es la categoría en vivo. "${previousLive.name}" dejó de estar en vivo.`
            : `"${activeCategory.name}" ahora es la categoría en vivo.`,
        );
      } else {
        toastSuccess(`"${activeCategory.name}" ya no está en vivo.`);
      }
      qc.invalidateQueries({ queryKey: ['event-categories'] });
      qc.invalidateQueries({ queryKey: orderKey });
    },
    onError: (err: any) => toastError(err?.response?.data?.error ?? 'No se pudo marcar la categoría en vivo.'),
  });

  const dragSnapshot = useRef<LaunchOrderTeam[] | undefined>(undefined);

  function onDragStart() {
    if (dragSnapshot.current) return;
    dragSnapshot.current = qc.getQueryData<LaunchOrderTeam[]>(orderKey);
  }

  function moveTeam(dragId: string, beforeId: string | null, status: CccdLaunchStatus) {
    qc.setQueryData<LaunchOrderTeam[]>(orderKey, (prev) => {
      if (!prev) return prev;
      const fromIndex = prev.findIndex((t) => t.id === dragId);
      if (fromIndex === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      const updated: LaunchOrderTeam = moved.launchStatus === status ? moved : { ...moved, launchStatus: status };
      if (beforeId) {
        const toIndex = next.findIndex((t) => t.id === beforeId);
        next.splice(toIndex === -1 ? next.length : toIndex, 0, updated);
      } else {
        next.push(updated);
      }
      return next;
    });
  }

  // Arrastre cancelado (Esc o soltado fuera de una columna): se vuelve al orden previo sin guardar nada.
  function onCancel() {
    const snapshot = dragSnapshot.current;
    dragSnapshot.current = undefined;
    if (snapshot) rollbackOptimistic(qc, orderKey, snapshot);
  }

  function changeStatus(teamId: string, status: CccdLaunchStatus) {
    onDragStart();
    moveTeam(teamId, null, status);
    void onDrop();
  }

  async function onDrop() {
    const snapshot = dragSnapshot.current;
    dragSnapshot.current = undefined;
    const current = qc.getQueryData<LaunchOrderTeam[]>(orderKey);
    if (!snapshot || !current) return;

    const statusChanges = current.filter((t) => snapshot.find((s) => s.id === t.id)?.launchStatus !== t.launchStatus);
    // launchOrder es 1-based (updateCccdLaunchOrderSchema exige min(1) — ver
    // también el auto-asignado de /categories/:categoryId/order, que empieza
    // en maxExisting + 1, nunca en 0).
    const orderChanged = current.some((t, index) => (snapshot.find((s) => s.id === t.id)?.launchOrder ?? -1) !== index + 1);
    if (statusChanges.length === 0 && !orderChanged) return;

    // allSettled: si una de las llamadas falla las demás igual se aplicaron en el servidor, así que
    // siempre se vuelve a pedir el orden real en vez de dejar la pantalla distinta de la base.
    const tasks: { label: string; run: Promise<unknown> }[] = [
      ...statusChanges.map((t) => ({ label: t.name, run: cccdScoring.setTeamLaunchStatus(t.id, t.launchStatus) })),
      ...(orderChanged
        ? [{ label: 'el orden de lanzamiento', run: cccdScoring.setCategoryLaunchOrder(activeCategoryId, current.map((t, index) => ({ teamId: t.id, launchOrder: index + 1 }))) }]
        : []),
    ];
    const results = await Promise.allSettled(tasks.map((t) => t.run));
    const failed = tasks.filter((_, i) => results[i].status === 'rejected').map((t) => t.label);
    if (failed.length > 0) {
      rollbackOptimistic(qc, orderKey, snapshot);
      toastError(`No se pudo guardar el cambio de ${failed.join(', ')}. Se muestra el estado actual del servidor.`);
    }
    invalidateOrder();
  }

  return (
    <SectionHeader
      title="Control de lanzamiento"
      subtitle="Arrastra un equipo entre columnas o cambia su estado con el selector de cada tarjeta; dentro de una columna, arrástralo para reordenarlo"
      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4.5L6 21l1.5-7.5L2 9h7z" /></svg>}
    >
      {categories.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Este evento no tiene categorías configuradas todavía.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategoryId(cat.id)}
                className={`min-h-11 rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors sm:min-h-0 ${
                  activeCategoryId === cat.id
                    ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-400'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {activeCategory && (
            <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3 dark:border-gray-800">
              <div>
                <p className="text-[13px] font-medium text-gray-800 dark:text-white/90">Categoría en vivo</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Es la que ven las páginas públicas de en-vivo</p>
              </div>
              <Toggle
                checked={!!activeCategory.isLive}
                disabled={liveMutation.isPending}
                onChange={(checked) => liveMutation.mutate(checked)}
                label=""
                ariaLabel={`Categoría en vivo: ${activeCategory.name}`}
              />
            </div>
          )}

          {isLoading ? (
            <LoadState />
          ) : teams.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
              Sin equipos en esta categoría.
            </p>
          ) : (
            <div className="-mx-6 overflow-x-auto custom-scrollbar">
              <div className="flex gap-3 px-6 pb-1">
                {LAUNCH_STATUSES.map((status) => {
                  const columnTeams = teams.filter((t) => t.launchStatus === status);
                  return (
                    <div key={status} className="flex w-[220px] shrink-0 flex-col rounded-xl bg-gray-50 p-2.5 dark:bg-white/[0.02]">
                      <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
                        <StatusPill tone={LAUNCH_STATUS_TONES[status]} label={LAUNCH_STATUS_LABELS[status]} />
                        <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{columnTeams.length}</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        {columnTeams.map((team) => (
                          <KanbanCard
                            key={team.id}
                            team={team}
                            onHoverCard={moveTeam}
                            onDragStart={onDragStart}
                            onDrop={onDrop}
                            onCancel={onCancel}
                            onChangeStatus={(next) => changeStatus(team.id, next)}
                          />
                        ))}
                      </div>
                      <KanbanColumnDropZone status={status} onHoverColumn={(dragId, s) => moveTeam(dragId, null, s)} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </SectionHeader>
  );
}
